'use client';

import { Modal } from '@1debit/chimekit';
import { Info } from '@1debit/icons';
import { useState } from 'react';
import Button from '../Buttons/BaseButton';
import { ContentfulRichTextBlock, Sys } from 'libs/contentful/types';
import RichTextCopy from '../RichTextCopy/RichTextCopy';

import { liveUpdateAttrs } from 'libs/contentful/utils';

export interface BaseModalProps {
  sys?: Sys;
  title: string;
  body: string | ContentfulRichTextBlock;
  openButtonText?: string;
  iconPosition?: 'before' | 'after';
  iconOnlyLink?: boolean;
}

const BaseModal = ({ title, body, openButtonText, iconPosition = 'before', iconOnlyLink, sys }: BaseModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const mainText = typeof body === 'string' ? <p>{body}</p> : <RichTextCopy copy={body} />;
  const afterIconStyles = iconPosition === 'after' ? 'align-top ml-[6px]' : '';
  const infoIcon = <Info className={`inline-block h-[18px] w-[18px] ${afterIconStyles}`} />;
  const interactiveInfoIcon = (
    <Info
      role="button"
      tabIndex={0}
      className={`inline-block h-[18px] w-[18px] cursor-pointer ${afterIconStyles}`}
      aria-label={openButtonText ?? title}
      onClick={() => setIsOpen(true)}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsOpen(true);
        }
      }}
    />
  );

  return (
    <>
      <span className="inline-block align-middle">
        {iconOnlyLink ? (
          <span
            className={`font-sans text-legal leading-[1.65] font-medium w-fit align-middle py-1 ${iconPosition === 'before' ? 'flex justify-center gap-[6px]' : 'inline'}`}
            {...liveUpdateAttrs(sys?.id, 'disclaimerText')}>
            {iconPosition === 'before' && interactiveInfoIcon}
            {openButtonText}
            {iconPosition === 'after' && (
              <>
                {'\u2060'}
                {interactiveInfoIcon}
              </>
            )}
          </span>
        ) : (
          <button
            className={`font-sans text-legal leading-[1.65] font-medium w-fit align-middle py-1 cursor-pointer ${iconPosition === 'before' ? 'flex justify-center gap-[6px]' : 'inline'}`}
            onClick={() => setIsOpen(true)}
            {...liveUpdateAttrs(sys?.id, 'disclaimerText')}>
            {iconPosition === 'before' && infoIcon}
            {openButtonText && (
              <>
                {openButtonText}
                {iconPosition === 'before' && <> ›</>}
              </>
            )}
            {iconPosition === 'after' && (
              <>
                {'\u2060'}
                {infoIcon}
              </>
            )}
          </button>
        )}
      </span>
      <Modal analyticsId={null} open={isOpen} onOpenChange={setIsOpen}>
        <Modal.Content className="sm:pb-10 sm:pt-5 sm:px-[60px] box-content sm:rounded-[10px] data-[state=open]:translate-y-0 animate-modal-slide-in">
          <Modal.Body className="sm:px-0">
            <Modal.Title className="text-heading-md font-sans" {...liveUpdateAttrs(sys?.id, 'headline')}>
              {title}
            </Modal.Title>
            <div className="font-serif my-[10px] text-body-serif" {...liveUpdateAttrs(sys?.id, 'copy')}>
              {mainText}
            </div>
          </Modal.Body>
          <Modal.Footer className="sm:pb-0 sm:pl-0">
            <div className="sm:w-1/2 w-full ">
              <Button onClick={() => setIsOpen(false)} variant="outline" className="border border-kale">
                Close
              </Button>
            </div>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default BaseModal;
